import { memo, useMemo } from "react";
import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import { BaseCard } from "./BaseCard";
import { COLORS } from "./constants";
import type { DistributionItem } from "@/lib/utils";

interface PieCardProps {
  title: string;
  items: DistributionItem[];
  reportLink: string;
  /** How `count` is shown; plain number by default. */
  formatValue?: (value: number) => string;
  /** Shown instead of the empty state while the items are still coming. */
  loading?: boolean;
}

export const PieCard = memo(
  ({ title, items, reportLink, formatValue = String, loading = false }: PieCardProps) => {
    const displayItems = useMemo(() => items.slice(0, 6), [items]);

    if (items.length === 0) {
      return (
        <BaseCard title={title} reportLink={reportLink}>
          <div className="flex flex-1 items-center justify-center text-sm text-muted-foreground">
            {loading ? "Loading…" : "No data"}
          </div>
        </BaseCard>
      );
    }

    return (
      <BaseCard title={title} reportLink={reportLink}>
        <div className="h-[124px] w-full shrink-0">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={displayItems}
                innerRadius={30}
                outerRadius={55}
                paddingAngle={2}
                dataKey="count"
                stroke="none"
                isAnimationActive={true}
              >
                {displayItems.map((_, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={COLORS[index % COLORS.length]}
                  />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="flex-1 space-y-2 overflow-hidden">
          {displayItems.map((item, index) => (
            <div
              key={item.label}
              className="flex justify-between items-center text-sm"
            >
              <div className="flex items-center gap-2 max-w-[70%]">
                <div
                  className="w-2 h-2 rounded-full shrink-0"
                  style={{ backgroundColor: COLORS[index % COLORS.length] }}
                />
                <span
                  className="block truncate font-medium text-foreground/80"
                  title={item.label}
                >
                  {item.label}
                </span>
              </div>
              <div className="flex items-center gap-1.5 text-sm shrink-0">
                <span className="font-bold tabular-nums text-foreground">
                  {formatValue(item.count)}
                </span>
                <span className="text-xs font-normal text-muted-foreground tabular-nums">
                  ({item.percentage}%)
                </span>
              </div>
            </div>
          ))}
        </div>
      </BaseCard>
    );
  },
);

PieCard.displayName = "PieCard";
